import React, { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import APIServices from '../../../../../../utils/services/EveryDoggyAPIServices';
import errorLogger from '../../../../../../utils/errorLogger/appErrorLogger';
import ComposeStripeViewModel, { IStripeViewModel } from './viewModel';
import { RootState } from '../../../../../../store';
import AppleGooglePayment from './components/appleGooglePayment';
import Paypal from './components/paypal';
import PaymentCard from './components/paymentCard';
import locales from '../../../../../../content.json';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { getParameterFromURL } from '../../../../../../utils/scripts';

import s from './index.module.scss';

import applePayIcn from './img/applePayIcn.png';
import applePayIcnDark from './img/applePayIcnDark.png';
import payPalIcon from './img/payPalIcon.png';
import gpl from './img/gpl.png';
import gplDark from './img/gplDark.png';
import cardsIcon from './img/cardsIcn.png';
import secure from './img/srcure.png';
import stripe from './img/stripe.png';

type PaymentMethod = 'apple' | 'card' | 'paypal';

const Checkout: FC = () => {
  const product = useSelector((state: RootState) => state.data.paymentModal.product);
  const [activeBlock, setActiveBlock] = useState(0);
  const stripePlanId = product.details ? product.details.stripe_plan_id : '';
  const [appleGooglepayActive, setAppleGooglepayActive] = useState(true);
  const [applePayShow, setApplePayShow] = useState(true);
  const showPDFUpsellingSistem = useSelector((state: RootState) => state.navigation.purchaseFlowScreen.payment_system);
  const darkTheme = getParameterFromURL('darkTheme');

  const paymentOrder: PaymentMethod[] = useMemo(() => {
    const defaultOrder: PaymentMethod[] = ['apple', 'card', 'paypal'];

    if (!showPDFUpsellingSistem) return defaultOrder;

    if (showPDFUpsellingSistem === 'applePay') {
      return ['apple', 'card', 'paypal'];
    }

    if (showPDFUpsellingSistem === 'paypal') {
      return ['paypal', 'apple', 'card'];
    }

    if (showPDFUpsellingSistem === 'cart') {
      return ['card', 'apple', 'paypal'];
    }

    return defaultOrder;
  }, [showPDFUpsellingSistem]);

  useEffect(() => {
    if (!showPDFUpsellingSistem) return;

    const index = paymentOrder.findIndex(m => {
      if (showPDFUpsellingSistem === 'applePay') return m === 'apple';
      if (showPDFUpsellingSistem === 'paypal') return m === 'paypal';
      if (showPDFUpsellingSistem === 'cart') return m === 'card';
      return false;
    });

    if (index !== -1) {
      setActiveBlock(index);
    }
  }, [paymentOrder, showPDFUpsellingSistem]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const isAppleReady = Boolean(window.paymentRequestApplePayValue);
      const isAppleButtonAvailable = Boolean(window.paymentApplePay);

      setAppleGooglepayActive(prev =>
        prev !== isAppleReady ? isAppleReady : prev
      );

      setApplePayShow(prev =>
        prev !== isAppleButtonAvailable ? isAppleButtonAvailable : prev
      );
    }, 300);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!appleGooglepayActive && paymentOrder[activeBlock] === 'apple') {
      setActiveBlock(
        paymentOrder.findIndex(m => m !== 'apple')
      );
    }
  }, [appleGooglepayActive, activeBlock, paymentOrder]);

  const viewModel: IStripeViewModel = ComposeStripeViewModel(
    window.location.href,
    stripePlanId,
    APIServices,
    errorLogger
  );

  const noPaypal = useSelector((state: RootState) => state.data.noPaypal);
  const [disableButton, setDisableButton] = useState(true);
  const [cardEmail, setCardEmail] = useState(viewModel.initialEmail());
  const [stripeError, setStripeError] = useState('');

  const stripePromise = useMemo(() => {
    return viewModel.getStripeKey().then(stripeKey => loadStripe(stripeKey)).catch(() => null);
  }, []);

  const handleToggle = (index: number) => {
    if (activeBlock !== index) {
      setActiveBlock(index);
    }
  };

  const handleCardChange = (value: boolean, email: string) => {
    setDisableButton(!value);
    setCardEmail(email);
  };

  const title1 = useMemo(() => {
    if(applePayShow) {
      return 'Apple Pay';
    } else {
      return 'Google Pay';
    }
  }, [applePayShow]);

    const locale = navigator.language.startsWith('ja') || navigator.language.startsWith('ko') ? 'en' : navigator.language.slice(0, 2);
    const options: StripeElementsOptions = {
      locale: locale as StripeElementsOptions['locale'],
    };

  return (
    <Elements stripe={stripePromise} options={options}>
       <div className={s.wrapper}>
        {paymentOrder.map((method, index) => {
          const isActive = activeBlock === index;

          if (method === 'apple') {
            return (
              <div
                key="apple"
                className={s.block}
                style={{ display: appleGooglepayActive ? 'block' : 'none' }}
              >
                <div className={s.header} onClick={() => handleToggle(index)}>
                  <input
                    type="radio"
                    checked={isActive}
                    onChange={() => handleToggle(index)}
                    className={s.radio}
                  />
                  <label className={`${s.radioLabel} ${isActive ? s.radioChecked : ''}`}>
                    <p>{title1}</p>
                    <img
                      className={applePayShow ? '' : s.gpl}
                      src={
                        applePayShow
                          ? darkTheme
                            ? applePayIcnDark
                            : applePayIcn
                          : darkTheme
                          ? gplDark
                          : gpl
                      }
                      alt="apple or google pay"
                    />
                  </label>
                </div>

                <div className={`${s.content} ${isActive ? s.active : ''}`}>
                  <AppleGooglePayment />
                  <div className={`${s.paypalWarnings} applepayWarnings`}></div>
                  <p className={s.secureText}>
                    {locales[window.userLang].payment_text_93}
                  </p>
                </div>
              </div>
            );
          }

          if (method === 'card') {
            return (
              <div key="card" className={s.block}>
                <div className={s.header} onClick={() => handleToggle(index)}>
                  <input
                    type="radio"
                    checked={isActive}
                    onChange={() => handleToggle(index)}
                    className={s.radio}
                  />
                  <label className={`${s.radioLabel} ${isActive ? s.radioChecked : ''}`}>
                    <p>{locales[window.userLang].payment_text_105}</p>
                    <img src={cardsIcon} alt="cards" />
                  </label>
                </div>

                <div className={`${s.content} ${isActive ? s.active : ''}`}>
                  <PaymentCard
                    cardEmail={cardEmail}
                    viewModel={viewModel}
                    disabled={disableButton}
                    setError={setStripeError}
                    onCardCompleted={handleCardChange}
                  />
                  {stripeError && <div className={s.error}>{stripeError}</div>}
                  <p className={s.secureText}>
                    {locales[window.userLang].payment_text_93}
                  </p>
                  <div className={s.imgWrapper}>
                    <img className={s.secureImg} src={secure} alt="secure ssl encryption" />
                    <img className={s.stripeImg} src={stripe} alt="Powered by stripe" />
                  </div>
                </div>
              </div>
            );
          }

          if (method === 'paypal') {
            return (
              <div key="paypal" className={s.block}>
                <div className={s.header} onClick={() => handleToggle(index)}>
                  <input
                    type="radio"
                    checked={isActive}
                    onChange={() => handleToggle(index)}
                    className={s.radio}
                  />
                  <label className={`${s.radioLabel} ${isActive ? s.radioChecked : ''}`}>
                    <p>PayPal</p>
                    <img src={payPalIcon} alt="paypal" />
                  </label>
                </div>

                <div className={`${s.content} ${isActive ? s.active : ''}`}>
                  {!noPaypal && (
                    <div className={s.buttonwrapper}>
                      <Paypal />
                    </div>
                  )}
                  <div className={`${s.paypalWarnings} paypalWarnings`}></div>
                  <p className={s.secureText}>
                    {locales[window.userLang].payment_text_93}
                  </p>
                  <img className={s.secureImg} src={secure} alt="secure ssl encryption" />
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>
    </Elements>
   
  );
}

export default Checkout;
